/** コマ割り（4コマ）モードの設定パネル */

import type { KomaSettings } from '../../lib/postTypes'
import { useKomaNumberStyles } from '../../lib/queries'
import { errorMessage } from '../../lib/api'
import { choiceCls, type SectionProps } from './types'

/**
 * 番号バッジの見た目（Tailwind クラス・フォント）だけをローカルに持つ。
 * キーとラベルの真実源はサーバー（GET /settings/koma-number-styles・koma.ts）—
 * 従来はキー/ラベルごとハードコード複製されドリフトの温床だった
 */
const NUMBER_STYLE_LOOK: Record<string, { cls: string; font?: string }> = {
  white_circle: { cls: 'bg-white text-slate-900 border border-slate-300' },
  black_circle: { cls: 'bg-slate-900 text-white font-bold' },
  brand_blue: { cls: 'bg-blue-600 text-white' },
  red_pop: { cls: 'bg-red-500 text-white font-black' },
  gold_elegant: { cls: 'bg-transparent text-amber-600 border border-amber-500', font: 'serif' },
  handwritten: { cls: 'bg-transparent text-slate-700 border border-slate-500 border-dashed', font: 'cursive' },
}
const NUMBER_STYLE_LOOK_FALLBACK = { cls: 'bg-white text-slate-900 border border-slate-300' }

export const DEFAULT_KOMA: KomaSettings = {
  probability: 0, format: 'vertical', panels: 4, gridOrder: 'z', numbering: true, numberPosition: 'top_left',
}

export function KomaPanel({ draft, setDraft }: SectionProps) {
  const numberStyles = useKomaNumberStyles()
  const enabled = draft.image.enabled
  const koma = draft.image.koma ?? DEFAULT_KOMA
  const setKoma = (patch: Partial<KomaSettings>) =>
    setDraft({ ...draft, image: { ...draft.image, koma: { ...koma, ...patch } } })

  return (
    <div className="gm-card p-4 md:p-5" data-testid="koma-settings">
      <p className="font-bold">コマ割り・4コマモード</p>
      {!enabled && <p className="mt-1"><span className="gm-chip gm-chip-neutral">画像生成がオフのため、今は使われません</span></p>}
      <p className="mt-1 text-sm text-ink-muted">
        設定した確率で、1枚絵の代わりに連続コマ（4コマ漫画のような構成）で画像を生成します。
        実写・漫画・図解などすべてのスタイルで発動します。
      </p>

      <div className="mt-3 space-y-4 text-sm">
        {/* 発動確率 */}
        <div>
          <p className="font-medium">発動確率（0% = オフ）</p>
          <div className="mt-1 flex items-center gap-3">
            <input type="range" min={0} max={100} step={5} value={koma.probability}
              disabled={!enabled} aria-label="発動確率"
              onChange={e => setKoma({ probability: Number(e.target.value) })}
              className="min-w-0 flex-1 accent-primary" />
            <input type="number" min={0} max={100} value={koma.probability}
              disabled={!enabled} aria-label="発動確率（%）"
              onChange={e => setKoma({ probability: Math.max(0, Math.min(100, Math.round(Number(e.target.value)))) })}
              className="gm-input gm-num w-16 text-right" />
            <span className="text-ink-muted">%</span>
          </div>
        </div>

        {/* フォーマット以下は確率0%でも常に表示（事前にプリセットしておける） */}
        <div>
          <p className="font-medium">フォーマット</p>
          <div className="mt-1 flex flex-wrap gap-2">
            {([
              { key: 'vertical', label: '縦型（縦に並ぶ）' },
              { key: 'grid', label: '四角（2×2の4コマ）' },
            ] as const).map(f => (
              <button key={f.key} disabled={!enabled} aria-pressed={koma.format === f.key}
                onClick={() => setKoma({ format: f.key })}
                className={choiceCls(koma.format === f.key)}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* 縦型: コマ数 */}
        {koma.format === 'vertical' && (
          <div>
            <p className="font-medium">コマ数</p>
            <div className="mt-1 flex flex-wrap gap-2">
              {[2, 3, 4, 5].map(n => (
                <button key={n} disabled={!enabled} aria-pressed={koma.panels === n}
                  onClick={() => setKoma({ panels: n })}
                  className={choiceCls(koma.panels === n)}>
                  <span className="gm-num">{n}</span>コマ
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 四角: 読み順 */}
        {koma.format === 'grid' && (
          <div>
            <p className="font-medium">コマの進み方（読み順）</p>
            <div className="mt-1 flex flex-wrap gap-2">
              {([
                { key: 'z', label: 'Z型（横読み: 左上→右上→左下→右下）' },
                { key: 'n', label: 'N型（縦読み: 右上→右下→左上→左下）' },
              ] as const).map(o => (
                <button key={o.key} disabled={!enabled} aria-pressed={koma.gridOrder === o.key}
                  onClick={() => setKoma({ gridOrder: o.key })}
                  className={choiceCls(koma.gridOrder === o.key)}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 番号 */}
        <div>
          <label className="flex items-center gap-2 font-medium">
            <input type="checkbox" className="accent-primary" checked={koma.numbering} disabled={!enabled}
              onChange={e => setKoma({ numbering: e.target.checked })} />
            コマに番号を振る（読み順の迷いを防ぐ）
          </label>
          {koma.numbering && (
            <div className="mt-2 space-y-3 pl-6">
              <div className="flex flex-wrap items-center gap-2">
                <label htmlFor="koma-number-position" className="text-ink-muted">番号の位置:</label>
                <select id="koma-number-position" value={koma.numberPosition} disabled={!enabled}
                  onChange={e => setKoma({ numberPosition: e.target.value as KomaSettings['numberPosition'] })}
                  className="gm-input w-auto">
                  <option value="top_left">コマの左上</option>
                  <option value="top_right">コマの右上</option>
                  <option value="bottom_left">コマの左下</option>
                  <option value="bottom_right">コマの右下</option>
                </select>
              </div>
              <div data-testid="koma-number-style">
                <p className="text-ink-muted">番号のデザイン:</p>
                {numberStyles.isError && (
                  <p className="mt-1 text-bad">番号デザインの一覧を読み込めませんでした: {errorMessage(numberStyles.error)}</p>
                )}
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {(numberStyles.data ?? []).map(s => {
                    const look = NUMBER_STYLE_LOOK[s.key] ?? NUMBER_STYLE_LOOK_FALLBACK
                    const active = (koma.numberStyle ?? 'white_circle') === s.key
                    return (
                      <button key={s.key} disabled={!enabled} aria-pressed={active}
                        onClick={() => setKoma({ numberStyle: s.key })}
                        className={`flex items-center gap-2 ${choiceCls(active)}`}>
                        <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${look.cls}`}
                          style={look.font ? { fontFamily: look.font } : undefined}>1</span>
                        {s.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        <p className="text-xs text-ink-muted">
          キャンバスは縦型 = 縦長（9:16）、四角 = 正方形で生成します。
        </p>
      </div>
    </div>
  )
}
